// Icon-only button with built-in tooltip + accessible name.
//
// Usage:
//   <IconButton label={t("actions.edit")} onClick={...}>
//     <Pencil className="h-4 w-4" />
//   </IconButton>
//
// Why: every icon-only Button in this codebase needs (a) a visible tooltip
// for sighted mouse users and (b) an aria-label for screen readers. Doing
// both manually was easy to forget, so this wrapper makes the right thing
// the default — pass `label`, get both. `label` should always come from
// i18n (t("actions.*")) so French and English render the same surface.
//
// If you actively want a button without a tooltip (rare — usually a
// design smell), use the raw <Button> instead.
import * as React from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export type IconButtonProps = Omit<ButtonProps, "aria-label"> & {
  label: string;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  tooltipDelay?: number;
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, tooltipSide = "top", tooltipDelay = 200, size = "icon", children, ...rest }, ref) => {
    return (
      <Tooltip delayDuration={tooltipDelay}>
        <TooltipTrigger asChild>
          <Button ref={ref} aria-label={label} size={size} {...rest}>
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide}>{label}</TooltipContent>
      </Tooltip>
    );
  },
);
IconButton.displayName = "IconButton";

export default IconButton;
